import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  FormControlLabel,
  Switch,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Chip,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Devices as DevicesIcon,
  SettingsRemote as RemoteIcon,
  DirectionsCar as CarIcon,
} from '@mui/icons-material';
import { settingsAPI } from '../services/api';

const VisibilitySettingsPage = () => {
  const [settings, setSettings] = useState({
    hide_devices: false,
    hide_active_devices: false,
    hide_vehicles: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getCompanySettings();
      setSettings({
        hide_devices: response.data.hide_devices || false,
        hide_active_devices: response.data.hide_active_devices || false,
        hide_vehicles: response.data.hide_vehicles || false,
      });
      setError(null);
    } catch (err) {
      console.error('Error loading visibility settings:', err);
      setError('Erreur lors du chargement des paramètres de visibilité');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (settingName) => (event) => {
    setSettings(prev => ({
      ...prev,
      [settingName]: event.target.checked
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      
      await settingsAPI.updateVisibilitySettings(settings);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving visibility settings:', err);
      setError('Erreur lors de la sauvegarde des paramètres');
    } finally {
      setSaving(false);
    }
  };

  const getVisibilityStatus = () => {
    if (settings.hide_devices && settings.hide_active_devices && settings.hide_vehicles) {
      return { label: 'Tout caché', color: 'error', icon: <VisibilityOffIcon /> };
    }
    if (settings.hide_devices || settings.hide_active_devices || settings.hide_vehicles) {
      return { label: 'Partiellement caché', color: 'warning', icon: <VisibilityOffIcon /> };
    }
    return { label: 'Tout visible', color: 'success', icon: <VisibilityIcon /> };
  };

  if (loading) {
    return (
      <Container maxWidth={false} sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  const visibilityStatus = getVisibilityStatus();

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Paramètres de Visibilité
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadSettings}
            disabled={loading}
          >
            Actualiser
          </Button>
        </Box>
      </Box>

      {/* Status Card */}
      <Card sx={{ mb: 3, bgcolor: 'background.default' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {visibilityStatus.icon}
            <Typography variant="h6">
              État actuel: 
            </Typography>
            <Chip 
              label={visibilityStatus.label}
              color={visibilityStatus.color}
              variant="outlined"
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Ces paramètres contrôlent ce que les clients peuvent voir dans leur portail.
          </Typography>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Paramètres de visibilité mis à jour avec succès!
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Devices Settings */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DevicesIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">
                  Dispositifs
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.hide_devices}
                      onChange={handleSettingChange('hide_devices')}
                      color="primary"
                    />
                  }
                  label="Cacher tous les dispositifs"
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.hide_active_devices}
                      onChange={handleSettingChange('hide_active_devices')}
                      color="primary"
                      disabled={settings.hide_devices}
                    />
                  }
                  label="Cacher les dispositifs actifs"
                />
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {settings.hide_devices 
                  ? "Les clients ne verront aucun dispositif dans leur portail."
                  : settings.hide_active_devices
                  ? "Les clients ne verront que les dispositifs inactifs."
                  : "Les clients verront tous leurs dispositifs."
                }
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Vehicles Settings */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CarIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">
                  Véhicules
                </Typography>
              </Box>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.hide_vehicles}
                    onChange={handleSettingChange('hide_vehicles')}
                    color="primary"
                  />
                }
                label="Cacher tous les véhicules"
              />
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {settings.hide_vehicles
                  ? "Les clients ne verront aucun véhicule dans leur portail."
                  : "Les clients verront tous leurs véhicules."
                }
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Info Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <VisibilityIcon sx={{ mr: 1, color: 'info.main' }} />
                <Typography variant="h6">
                  Information
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary">
                <strong>Note importante:</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                • Ces paramètres n'affectent que les utilisateurs clients
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Les administrateurs voient toujours toutes les données
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Les changements prennent effet immédiatement
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Les clients verront des listes vides quand les éléments sont cachés
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Save Button */}
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{ minWidth: 200 }}
        >
          {saving ? 'Sauvegarde...' : 'Sauvegarder les paramètres'}
        </Button>
      </Box>

      {/* Current Settings Summary */}
      <Paper sx={{ mt: 4, p: 3, bgcolor: 'grey.50' }}>
        <Typography variant="h6" gutterBottom>
          Résumé des paramètres actuels
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Chip 
              icon={settings.hide_devices ? <VisibilityOffIcon /> : <VisibilityIcon />}
              label={`Dispositifs: ${settings.hide_devices ? 'Cachés' : 'Visibles'}`}
              color={settings.hide_devices ? 'error' : 'success'}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Chip 
              icon={settings.hide_active_devices ? <VisibilityOffIcon /> : <VisibilityIcon />}
              label={`Actifs: ${settings.hide_active_devices ? 'Cachés' : 'Visibles'}`}
              color={settings.hide_active_devices ? 'error' : 'success'}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Chip 
              icon={settings.hide_vehicles ? <VisibilityOffIcon /> : <VisibilityIcon />}
              label={`Véhicules: ${settings.hide_vehicles ? 'Cachés' : 'Visibles'}`}
              color={settings.hide_vehicles ? 'error' : 'success'}
              variant="outlined"
            />
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default VisibilitySettingsPage;
